/**
 * E2E Encryption Setup Component
 *
 * Guides users through setting up end-to-end encryption
 */

import { useState } from 'react';
import { useE2EEncryption } from '../../../hooks/useE2EEncryption';
import { registerAlias } from '../api';

interface EncryptionSetupProps {
  userId: string;
  onComplete?: () => void;
}

export function EncryptionSetup({ userId, onComplete }: EncryptionSetupProps) {
  const {
    hasKeys,
    isRegistered,
    publicKey,
    deviceId,
    isInitializing,
    error,
    needsPassphrase,
    needsMigration,
    initializeKeys,
    unlockKeys,
    registerKeys,
    migrateKeys,
  } = useE2EEncryption();

  const [step, setStep] = useState<'intro' | 'passphrase' | 'confirm' | 'generating' | 'registering' | 'backup' | 'complete'>('intro');
  const [passphrase, setPassphrase] = useState('');
  const [passphraseConfirm, setPassphraseConfirm] = useState('');
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [hasDownloadedBackup, setHasDownloadedBackup] = useState(false);
  const [backupData, setBackupData] = useState<string | null>(null);

  const handleSetup = async () => {
    // Step 1: Prompt for passphrase
    setStep('passphrase');
  };

  const handlePassphraseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passphrase.length < 8) {
      alert('Passphrase must be at least 8 characters');
      return;
    }

    if (passphrase !== passphraseConfirm) {
      alert('Passphrases do not match');
      return;
    }

    try {
      // Step 2: Generate keys
      setStep('generating');
      await initializeKeys(userId, passphrase);

      // Step 3: Register keys with server
      setStep('registering');
      await registerKeys();

      // Step 3.5: Register alias WITH public key (atomic binding)
      console.log('Binding alias to public key...', { userId, publicKey });
      try {
        await registerAlias(userId, publicKey || '');
        console.log('Alias successfully bound to public key');
      } catch (aliasErr) {
        console.error('Failed to bind alias to public key:', aliasErr);
        // Continue anyway - user can retry later
      }

      // Step 4: FORCE BACKUP before completing
      // Create backup data
      const backup = {
        userId,
        deviceId,
        publicKey,
        passphrase, // User needs this to decrypt the backup
        created: new Date().toISOString(),
        warning: 'KEEP THIS SAFE! Without this backup and your passphrase, you cannot recover your messages.',
      };
      setBackupData(JSON.stringify(backup, null, 2));
      setStep('backup');
    } catch (err) {
      console.error('Encryption setup failed:', err);
      setStep('passphrase'); // Go back to passphrase entry
    }
  };

  const handleDownloadBackup = () => {
    if (!backupData) return;

    const blob = new Blob([backupData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `encryption-keys-backup-${userId}-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setHasDownloadedBackup(true);
  };

  const handleCompleteSetup = () => {
    setStep('complete');
    setTimeout(() => {
      onComplete?.();
    }, 2000);
  };

  if (hasKeys && isRegistered) {
    return (
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-100">
              Encryption Enabled
            </h3>
            <p className="text-sm text-green-700 dark:text-green-300 mt-1">
              Your messages are protected with end-to-end encryption
            </p>
          </div>
        </div>

        <div className="mt-4 bg-white dark:bg-gray-800 rounded p-3 text-xs font-mono break-all">
          <div className="text-gray-600 dark:text-gray-400">Public Key:</div>
          <div className="text-gray-900 dark:text-gray-100">{publicKey?.substring(0, 32)}...</div>
          <div className="text-gray-600 dark:text-gray-400 mt-2">Device ID:</div>
          <div className="text-gray-900 dark:text-gray-100">{deviceId}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      {step === 'intro' && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Enable End-to-End Encryption
            </h3>
          </div>

          {/* CRITICAL WARNING */}
          <div className="mb-6 bg-red-50 dark:bg-red-900/30 border-2 border-red-500 dark:border-red-600 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h4 className="text-red-900 dark:text-red-100 font-bold text-lg mb-2">
                  ⚠️ CRITICAL: You MUST Back Up Your Keys
                </h4>
                <p className="text-red-800 dark:text-red-200 mb-2 font-semibold">
                  Without a backup, losing your device or forgetting your passphrase means PERMANENT loss of all encrypted messages!
                </p>
                <p className="text-red-700 dark:text-red-300 text-sm">
                  We will force you to download a backup before completing setup. Keep it somewhere safe!
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3 mb-6 text-gray-700 dark:text-gray-300">
            <p className="font-medium">Protect your private messages with encryption:</p>
            <ul className="space-y-2 ml-4">
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Only you and your recipients can read messages</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Your private keys are encrypted with a passphrase</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Messages are encrypted before leaving your device</span>
              </li>
            </ul>
          </div>

          <button
            onClick={handleSetup}
            disabled={isInitializing}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
          >
            I Understand - Continue
          </button>
        </>
      )}

      {step === 'passphrase' && (
        <form onSubmit={handlePassphraseSubmit}>
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Create Encryption Passphrase
            </h3>
          </div>

          <div className="space-y-4 mb-6">
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <p className="text-sm text-yellow-800 dark:text-yellow-300">
                <strong>Important:</strong> Remember this passphrase! You'll need it to access your encrypted messages.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Passphrase (min. 8 characters)
              </label>
              <div className="relative">
                <input
                  type={showPassphrase ? 'text' : 'password'}
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter a strong passphrase"
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassphrase(!showPassphrase)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  {showPassphrase ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirm Passphrase
              </label>
              <input
                type={showPassphrase ? 'text' : 'password'}
                value={passphraseConfirm}
                onChange={(e) => setPassphraseConfirm(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Re-enter passphrase"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setStep('intro')}
              className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Back
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Continue
            </button>
          </div>
        </form>
      )}

      {step === 'generating' && (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
            <svg className="animate-spin w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Generating Encryption Keys
          </h4>
          <p className="text-gray-600 dark:text-gray-400">
            Creating your unique encryption keypair...
          </p>
        </div>
      )}

      {step === 'registering' && (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
            <svg className="animate-spin w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Registering Public Key
          </h4>
          <p className="text-gray-600 dark:text-gray-400">
            Sharing your public key with the server...
          </p>
        </div>
      )}

      {step === 'backup' && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Download Your Backup NOW
            </h3>
          </div>

          {/* CRITICAL WARNING */}
          <div className="mb-6 bg-red-50 dark:bg-red-900/30 border-2 border-red-500 dark:border-red-600 rounded-lg p-4">
            <div className="space-y-3">
              <p className="text-red-900 dark:text-red-100 font-bold text-lg">
                🚨 MANDATORY BACKUP REQUIRED
              </p>
              <p className="text-red-800 dark:text-red-200 font-semibold">
                You MUST download this backup file before continuing. Without it, you will PERMANENTLY lose access to all encrypted messages if:
              </p>
              <ul className="text-red-700 dark:text-red-300 text-sm space-y-1 ml-4">
                <li>• You lose or damage your device</li>
                <li>• You forget your passphrase</li>
                <li>• Your browser data is cleared</li>
                <li>• You switch to a new device</li>
              </ul>
              <p className="text-red-900 dark:text-red-100 font-bold mt-3">
                Store this backup file in a secure location (password manager, encrypted USB drive, etc.)
              </p>
            </div>
          </div>

          {/* Backup Data Preview */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Backup Contents (JSON)
            </label>
            <pre className="bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg p-4 overflow-auto max-h-60 text-xs font-mono text-gray-800 dark:text-gray-200">
{backupData}
            </pre>
          </div>

          {/* Download Button */}
          <div className="space-y-4">
            <button
              onClick={handleDownloadBackup}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Backup File
            </button>

            {hasDownloadedBackup && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Backup downloaded successfully!
                </p>
              </div>
            )}

            {/* Continue Button - Only Enabled After Download */}
            <button
              onClick={handleCompleteSetup}
              disabled={!hasDownloadedBackup}
              className={`w-full font-medium py-3 px-4 rounded-lg transition-colors ${
                hasDownloadedBackup
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              {hasDownloadedBackup ? 'I Have Saved My Backup - Continue' : 'Download Backup First'}
            </button>
          </div>
        </>
      )}

      {step === 'complete' && (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h4 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">
            Encryption Enabled!
          </h4>
          <p className="text-green-700 dark:text-green-300">
            Your messages are now protected
          </p>
        </div>
      )}

      {error && (
        <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}
    </div>
  );
}
